import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasksContext } from '../../../shared/contexts/TasksContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import { parseImportedTasks } from '../../tasks/utils/taskImportExport';
import { calculateOrderForParent } from '../../tasks/utils/taskUtils';
import { trackEvent } from '../../../shared/utils/analytics';

/**
 * Hook for importing tasks from text (paste or file).
 * Uses TasksContext and AuthContext. Returns importTasksFromText and helpers for the import popup.
 */
export function useImportTasks() {
  const navigate = useNavigate();
  const {
    tasks,
    currentFileId,
    createTask,
    fetchTasks,
    createFile,
    switchFile,
    updateTaskStatus,
  } = useTasksContext();
  const { isLoggedIn } = useAuth();

  const importTasksFromText = useCallback(
    async (text, targetFileId = null) => {
      try {
        const parsedTasks = parseImportedTasks(text);

        if (parsedTasks.length === 0) {
          alert('No valid tasks found.');
          return [];
        }

        if (isLoggedIn) {
          // Bulk import: one API call to import.php
          const payload = [];
          const taskStack = []; // { indentLevel, indexInPayload }

          for (let i = 0; i < parsedTasks.length; i++) {
            const parsedTask = parsedTasks[i];
            const { text: taskText, indentLevel, status, dueDate } = parsedTask;

            const safeTaskText =
              taskText !== null && taskText !== undefined ? String(taskText) : '';

            if (!safeTaskText.trim()) continue;

            let parentIndex = null;
            if (indentLevel > 0 && taskStack.length > 0) {
              for (let j = taskStack.length - 1; j >= 0; j--) {
                if (taskStack[j].indentLevel === indentLevel - 1) {
                  parentIndex = taskStack[j].indexInPayload;
                  break;
                }
              }
            }

            const safeDueDate =
              dueDate && dueDate.trim && dueDate.trim() !== '' ? dueDate : null;

            payload.push({
              text: safeTaskText,
              parent_index: parentIndex,
              status,
              due_date: safeDueDate,
              order: null,
            });

            while (
              taskStack.length > 0 &&
              taskStack[taskStack.length - 1].indentLevel >= indentLevel
            ) {
              taskStack.pop();
            }
            taskStack.push({ indentLevel, indexInPayload: payload.length - 1 });
          }

          if (payload.length === 0) {
            alert('No valid tasks found.');
            return [];
          }

          const parentIndices = new Set(
            payload
              .map((t) => t.parent_index)
              .filter((idx) => idx !== null && idx !== undefined)
          );
          parentIndices.forEach((idx) => {
            payload[idx].status = 'expanded';
          });

          const response = await fetch('/api/import.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              file_id: targetFileId,
              tasks: payload,
            }),
            credentials: 'include',
          });

          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || 'Import failed');
          }

          const data = await response.json();
          const createdTasks = data.tasks || [];
          await fetchTasks(targetFileId ?? currentFileId);
          trackEvent('tasks_imported', { task_count: createdTasks.length });
          return createdTasks;
        }

        // Logged out: create tasks locally (no API)
        const tasksForFile =
          targetFileId != null
            ? tasks.filter((t) => t.file_id === targetFileId)
            : tasks;
        const createdTasks = [];
        const taskStack = [];
        const lastTaskAtParent = new Map();

        for (let i = 0; i < parsedTasks.length; i++) {
          const parsedTask = parsedTasks[i];
          const { text: taskText, indentLevel, status, dueDate } = parsedTask;

          const safeTaskText =
            taskText !== null && taskText !== undefined ? String(taskText) : '';

          if (!safeTaskText.trim()) continue;

          let taskParentId = null;
          if (indentLevel > 0 && taskStack.length > 0) {
            for (let j = taskStack.length - 1; j >= 0; j--) {
              if (taskStack[j].indentLevel === indentLevel - 1) {
                taskParentId = taskStack[j].task.id;
                break;
              }
            }
          }

          const safeTaskParentId = taskParentId ?? null;
          const parentKey =
            safeTaskParentId === null ? 'null' : String(safeTaskParentId);
          const lastTask = lastTaskAtParent.get(parentKey);
          const currentTasksWithCreated = [...tasksForFile, ...createdTasks];
          const order = lastTask
            ? calculateOrderForParent(
                safeTaskParentId,
                currentTasksWithCreated,
                lastTask.id
              )
            : calculateOrderForParent(
                safeTaskParentId,
                currentTasksWithCreated
              );

          const safeDueDate =
            dueDate && dueDate.trim && dueDate.trim() !== '' ? dueDate : null;

          try {
            const newTask = await createTask(
              safeTaskText,
              safeTaskParentId,
              order,
              status,
              safeDueDate,
              targetFileId
            );
            if (newTask) {
              createdTasks.push(newTask);
              while (
                taskStack.length > 0 &&
                taskStack[taskStack.length - 1].indentLevel >= indentLevel
              ) {
                taskStack.pop();
              }
              taskStack.push({ task: newTask, indentLevel });
              lastTaskAtParent.set(parentKey, newTask);
            }
          } catch (taskError) {
            console.error(`Error creating task "${safeTaskText}":`, taskError);
          }
        }

        if (createdTasks.length > 0) {
          const parentIds = new Set(
            createdTasks
              .filter((t) => t.parent_task_id != null)
              .map((t) => t.parent_task_id)
          );
          createdTasks.forEach((t) => {
            if (parentIds.has(t.id)) {
              updateTaskStatus(t.id, 'expanded');
            }
          });
          trackEvent('tasks_imported', { task_count: createdTasks.length });
          return createdTasks;
        }
        alert('No tasks were created. Please check the format.');
        return [];
      } catch (error) {
        console.error('Error importing:', error);
        alert(
          `Error importing: ${error.message}. Please check the format.`
        );
        return [];
      }
    },
    [
      tasks,
      currentFileId,
      createTask,
      fetchTasks,
      isLoggedIn,
      updateTaskStatus,
    ]
  );

  const handleImportPasted = useCallback(
    async (text) => {
      try {
        if (!isLoggedIn) {
          await importTasksFromText(text);
          return;
        }
        const newFile = await createFile('Imported tasks', {
          switchToFile: false,
        });
        if (newFile?.id) {
          const createdTasks = await importTasksFromText(text, newFile.id);
          if (createdTasks.length > 0) {
            await switchFile(newFile.id);
            navigate(`/list/${newFile.id}`, { replace: true });
          }
        } else {
          await importTasksFromText(text);
        }
      } catch (err) {
        console.error('Error creating file for import:', err);
        alert(
          `Could not create file: ${err.message}. Importing into current file.`
        );
        await importTasksFromText(text);
      }
    },
    [
      importTasksFromText,
      createFile,
      isLoggedIn,
      switchFile,
      navigate,
    ]
  );

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        if (!isLoggedIn) {
          await importTasksFromText(text);
          return;
        }
        const baseName =
          file.name.replace(/\.[^.]+$/, '').trim() || 'Imported tasks';
        try {
          const newFile = await createFile(baseName, {
            switchToFile: false,
          });
          if (newFile?.id) {
            const createdTasks = await importTasksFromText(text, newFile.id);
            if (createdTasks.length > 0) {
              await switchFile(newFile.id);
              navigate(`/list/${newFile.id}`, { replace: true });
            }
          } else {
            await importTasksFromText(text);
          }
        } catch (err) {
          console.error('Error creating file for import:', err);
          alert(
            `Could not create file: ${err.message}. Importing into current file.`
          );
          await importTasksFromText(text);
        }
      } finally {
        e.target.value = '';
      }
    },
    [
      importTasksFromText,
      createFile,
      isLoggedIn,
      switchFile,
      navigate,
    ]
  );

  return {
    importTasksFromText,
    handleImportPasted,
    handleFileChange,
  };
}
